/**
 * ChromaKeyProcessor - Real-time green screen (chroma key) video processing
 *
 * This processor removes a specified color (typically green) from the video
 * and replaces it with a virtual background image.
 */

export class ChromaKeyProcessor {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.videoElement = null;
    this.animationFrameId = null;
    this.isRunning = false;
    this.outputStream = null;

    // Chroma key settings
    this.keyColor = { r: 0, g: 255, b: 0 }; // Default green
    this.similarity = 0.4; // 0-1, how similar colors need to be to be removed
    this.smoothness = 0.1; // 0-1, edge smoothing
    this.backgroundImage = null;
    this.backgroundCanvas = null;
    this.backgroundCtx = null;
  }

  /**
   * Convert hex color to RGB
   */
  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 0, g: 255, b: 0 };
  }

  /**
   * Calculate color distance (normalized 0-1)
   */
  colorDistance(r1, g1, b1, r2, g2, b2) {
    const maxDistance = Math.sqrt(255 * 255 * 3);
    const distance = Math.sqrt(
      Math.pow(r1 - r2, 2) +
      Math.pow(g1 - g2, 2) +
      Math.pow(b1 - b2, 2)
    );
    return distance / maxDistance;
  }

  /**
   * Initialize the processor
   */
  async init() {
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    this.backgroundCanvas = document.createElement('canvas');
    this.backgroundCtx = this.backgroundCanvas.getContext('2d');
    this.videoElement = document.createElement('video');
    this.videoElement.muted = true;
    this.videoElement.playsInline = true;
    console.log('ChromaKeyProcessor initialized');
  }

  /**
   * Load background image
   */
  async loadBackgroundImage(imageUrl) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        this.backgroundImage = img;
        console.log('Background image loaded:', imageUrl);
        resolve();
      };
      img.onerror = (err) => {
        console.error('Failed to load background image:', err);
        reject(err);
      };
      img.src = imageUrl;
    });
  }

  /**
   * Update chroma key configuration
   */
  updateConfig(config) {
    if (config.keyColor) {
      this.keyColor = typeof config.keyColor === 'string'
        ? this.hexToRgb(config.keyColor)
        : config.keyColor;
    }
    if (config.similarity !== undefined) {
      this.similarity = config.similarity / 100; // Convert percentage to 0-1
    }
    if (config.smoothness !== undefined) {
      this.smoothness = config.smoothness / 100; // Convert percentage to 0-1
    }
    console.log('ChromaKey config updated:', {
      keyColor: this.keyColor,
      similarity: this.similarity,
      smoothness: this.smoothness
    });
  }

  /**
   * Process a single frame
   */
  processFrame() {
    if (!this.isRunning || !this.videoElement || !this.ctx) return;

    const width = this.canvas.width;
    const height = this.canvas.height;

    // Draw the video frame
    this.ctx.drawImage(this.videoElement, 0, 0, width, height);

    // Get image data
    const imageData = this.ctx.getImageData(0, 0, width, height);
    const data = imageData.data;

    // Draw background if available
    if (this.backgroundImage) {
      this.backgroundCtx.drawImage(this.backgroundImage, 0, 0, width, height);
      const bgData = this.backgroundCtx.getImageData(0, 0, width, height).data;

      // Process each pixel
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        // Calculate distance from key color
        const distance = this.colorDistance(
          r, g, b,
          this.keyColor.r, this.keyColor.g, this.keyColor.b
        );

        // Determine transparency based on distance and similarity threshold
        if (distance < this.similarity) {
          // Replace with background pixel
          data[i] = bgData[i];
          data[i + 1] = bgData[i + 1];
          data[i + 2] = bgData[i + 2];
          data[i + 3] = 255;
        } else if (distance < this.similarity + this.smoothness) {
          // Blend for smooth edges
          const blend = (distance - this.similarity) / this.smoothness;
          data[i] = Math.round(data[i] * blend + bgData[i] * (1 - blend));
          data[i + 1] = Math.round(data[i + 1] * blend + bgData[i + 1] * (1 - blend));
          data[i + 2] = Math.round(data[i + 2] * blend + bgData[i + 2] * (1 - blend));
        }
      }
    } else {
      // No background - just make key color transparent
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        const distance = this.colorDistance(
          r, g, b,
          this.keyColor.r, this.keyColor.g, this.keyColor.b
        );

        if (distance < this.similarity) {
          data[i + 3] = 0; // Make transparent
        } else if (distance < this.similarity + this.smoothness) {
          const blend = (distance - this.similarity) / this.smoothness;
          data[i + 3] = Math.round(255 * blend);
        }
      }
    }

    // Put processed image back
    this.ctx.putImageData(imageData, 0, 0);

    // Continue processing
    this.animationFrameId = requestAnimationFrame(() => this.processFrame());
  }

  /**
   * Start processing
   * @param {MediaStream} inputStream - The input video stream
   * @param {Object} config - Chroma key configuration
   * @returns {MediaStream} - The processed output stream
   */
  async start(inputStream, config) {
    if (this.isRunning) {
      await this.stop();
    }

    // Apply configuration
    this.updateConfig(config);

    // Load background image if provided
    if (config.backgroundImageUrl) {
      await this.loadBackgroundImage(config.backgroundImageUrl);
    }

    // Set up video element with input stream
    this.videoElement.srcObject = inputStream;
    await this.videoElement.play();

    // Set canvas dimensions to match video
    const videoTrack = inputStream.getVideoTracks()[0];
    const settings = videoTrack.getSettings();
    this.canvas.width = settings.width || 640;
    this.canvas.height = settings.height || 480;
    this.backgroundCanvas.width = this.canvas.width;
    this.backgroundCanvas.height = this.canvas.height;

    console.log('ChromaKey processing started:', this.canvas.width, 'x', this.canvas.height);

    // Start processing loop
    this.isRunning = true;
    this.processFrame();

    // Create output stream from canvas
    this.outputStream = this.canvas.captureStream(30); // 30 FPS
    return this.outputStream;
  }

  /**
   * Stop processing
   */
  async stop() {
    this.isRunning = false;

    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    if (this.videoElement) {
      this.videoElement.srcObject = null;
    }

    if (this.outputStream) {
      this.outputStream.getTracks().forEach(track => track.stop());
      this.outputStream = null;
    }

    this.backgroundImage = null;
    console.log('ChromaKey processing stopped');
  }

  /**
   * Check if processor is running
   */
  get processorRunning() {
    return this.isRunning;
  }
}

export default ChromaKeyProcessor;
